import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { CourseWizard } from '../components/courses';
import { SlideEditor } from '../components/courses/SlideEditor';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  BookOpen,
  Plus,
  Search,
  Edit,
  Trash2,
  Video,
  FileText,
  HelpCircle,
  Loader2,
  CheckCircle2,
  XCircle,
  Upload,
  Link as LinkIcon,
  Sparkles,
  Brain,
  GraduationCap,
  Clock,
  Award,
  Users,
  Send,
  RefreshCw,
  ChevronRight,
  Play,
  Image,
  Eye,
  CircleDot,
  CheckSquare,
  ToggleLeft,
  GitMerge,
  ImagePlus,
  Minus,
  Settings,
  Timer,
  AlertTriangle,
  ChevronsUpDown,
  Check,
  X,
  CalendarIcon,
  FileIcon,
  Presentation,
  Music,
  ListOrdered,
  Save,
  MoreHorizontal,
  CheckCircle,
  Languages,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Onboarding',
  'Product Knowledge',
  'Sales Training',
  'Compliance',
  'Leadership',
  'Soft Skills',
  'Technical',
  'Seasonal',
];

const DIFFICULTY_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];

export const CourseManagement = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasFeature, edition, isPlatformUser } = useTenant();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('latest_created'); // Sorting state

  // Quiz Analytics
  const [quizAnalyticsOpen, setQuizAnalyticsOpen] = useState(false);
  const [quizAnalytics, setQuizAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Check if AI features are available
  const canUseAICourseGen = isPlatformUser || hasFeature('ai_course_generation') || edition === 'enterprise';
  const canUseAITranslation = isPlatformUser || hasFeature('ai_translation') || edition !== 'standard';
  const canUseAIQuizGen = isPlatformUser || hasFeature('ai_features') || edition !== 'standard';

  // Course Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEditingCourse, setWizardEditingCourse] = useState(null);

  // Course Dialog (kept for backward compatibility, will be removed)
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Delete Course State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: '',
    difficulty: 'Beginner',
    duration_minutes: 30,
    points_reward: 100,
    thumbnail_url: '',
    is_published: false,
  });

  // Module Dialog
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    title: '',
    type: 'video',
    content: '',
    video_url: '',
    content_url: '',
    duration_minutes: 10,
    order: 1,
  });
  const [savingModule, setSavingModule] = useState(false);
  const [slideEditorOpen, setSlideEditorOpen] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // Quiz Dialog
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [standaloneQuizDialogOpen, setStandaloneQuizDialogOpen] = useState(false);
  const [quizForm, setQuizForm] = useState({
    title: '',
    passing_score: 70,
    num_questions: 5,
    time_limit: 30, // in minutes, 0 = no limit
    max_attempts: 3, // 0 = unlimited
    show_correct_answers: true, // show correct answers after submission
    shuffle_questions: false,
    shuffle_options: false,
    questions: [],
    thumbnail_url: '',
  });
  const [standaloneQuizForm, setStandaloneQuizForm] = useState({
    title: '',
    description: '',
    category: 'Assessment',
    passing_score: 70,
    num_questions: 5,
    time_limit: 30,
    max_attempts: 3,
    show_correct_answers: true,
    shuffle_questions: false,
    shuffle_options: false,
    questions: [],
    thumbnail_url: '',
    is_published: false,
  });
  const [standaloneQuizStep, setStandaloneQuizStep] = useState(1); // 1: Quiz Details, 2: Assignment
  const [standaloneQuizzes, setStandaloneQuizzes] = useState([]);
  const [standaloneQuizAssignment, setStandaloneQuizAssignment] = useState({
    assign_to: 'role',
    roles: [],
    user_ids: [],
    due_date: '',
    skip_assignment: false,
  });
  const [createdQuizId, setCreatedQuizId] = useState(null);
  const [confirmCloseDialog, setConfirmCloseDialog] = useState({ open: false, type: null });
  const [questionTypeMenuOpen, setQuestionTypeMenuOpen] = useState(false);
  const [generatingQuiz, setGeneratingQuiz] = useState(false);
  const [savingQuiz, setSavingQuiz] = useState(false);
  const [uploadingQuizThumbnail, setUploadingQuizThumbnail] = useState(false);
  const quizThumbnailRef = useRef(null);

  // AI Video Generation
  const [videoGenDialogOpen, setVideoGenDialogOpen] = useState(false);
  const [videoGenForm, setVideoGenForm] = useState({
    title: '',
    prompt: '',
    duration: 4,
    size: '1280x720',
  });
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [generatedVideos, setGeneratedVideos] = useState([]);

  // Assignment Dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    assign_to: 'role',
    roles: [],
    user_ids: [],
    due_date: '',
  });
  const [users, setUsers] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [roleSelectOpen, setRoleSelectOpen] = useState(false);

  // Course Assignment Stats
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [courseStats, setCourseStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // AI Course Generation
  const [aiCourseDialogOpen, setAiCourseDialogOpen] = useState(false);
  const [aiCourseForm, setAiCourseForm] = useState({
    topic: '',
    category: 'sales',
    target_audience: 'all employees',
    difficulty: 'intermediate',
    duration_minutes: 30,
  });
  const [generatingAiCourse, setGeneratingAiCourse] = useState(false);

  // Course Translation
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [translateCourse, setTranslateCourse] = useState(null);
  const [translateForm, setTranslateForm] = useState({
    target_language: 'hi',
    fields: ['title', 'description', 'modules'],
  });
  const [translating, setTranslating] = useState(false);

  const SUPPORTED_LANGUAGES = [
    { code: 'hi', name: 'Hindi' },
    { code: 'ta', name: 'Tamil' },
    { code: 'te', name: 'Telugu' },
    { code: 'kn', name: 'Kannada' },
    { code: 'mr', name: 'Marathi' },
    { code: 'bn', name: 'Bengali' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'pa', name: 'Punjabi' },
  ];

  useEffect(() => {
    fetchCourses();
    fetchUsers();
    fetchStandaloneQuizzes();
  }, []);

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/courses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Sort courses by creation date (newest first)
      const sortedCourses = response.data.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Descending order
      });
      setCourses(sortedCourses);
    } catch (error) {
      // Fallback to regular courses endpoint
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/courses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Sort courses by creation date (newest first)
        const sortedCourses = response.data.sort((a, b) => {
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA; // Descending order
        });
        setCourses(sortedCourses);
      } catch (err) {
        console.error('Failed to fetch courses:', err);
        toast.error('Failed to load courses');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchStandaloneQuizzes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/standalone-quizzes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStandaloneQuizzes(response.data.quizzes || []);
    } catch (error) {
      console.error('Failed to fetch standalone quizzes:', error);
    }
  };

  const handleCreateCourse = () => {
    setWizardEditingCourse(null);
    setWizardOpen(true);
  };

  const handleEditCourse = (course) => {
    setWizardEditingCourse(course);
    setWizardOpen(true);
  };

  // AI Course Generation
  const handleGenerateAiCourse = async () => {
    if (!aiCourseForm.topic.trim()) {
      toast.error('Please enter a topic for the course');
      return;
    }
    
    setGeneratingAiCourse(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/ai/generate-course`, aiCourseForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success('AI Course generated successfully! Saved as draft.');
      setAiCourseDialogOpen(false);
      setAiCourseForm({
        topic: '',
        category: 'sales',
        target_audience: 'all employees',
        difficulty: 'intermediate',
        duration_minutes: 30,
      });
      fetchCourses(); // Refresh the list
    } catch (error) {
      console.error('Failed to generate AI course:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate course');
    } finally {
      setGeneratingAiCourse(false);
    }
  };

  // Course Translation
  const handleTranslateCourse = async () => {
    if (!translateCourse) return;
    
    setTranslating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/courses/${translateCourse.id}/translate`,
        translateForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Course translated to ${SUPPORTED_LANGUAGES.find(l => l.code === translateForm.target_language)?.name || translateForm.target_language}`);
      setTranslateDialogOpen(false);
      setTranslateCourse(null);
      fetchCourses();
    } catch (error) {
      console.error('Failed to translate course:', error);
      toast.error(error.response?.data?.detail || 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  const openTranslateDialog = (course) => {
    setTranslateCourse(course);
    setTranslateForm({
      target_language: 'hi',
      fields: ['title', 'description', 'modules'],
    });
    setTranslateDialogOpen(true);
  };

  // Legacy handlers (kept for backward compatibility)
  const handleCreateCourseLegacy = () => {
    setEditingCourse(null);
    setCourseForm({
      title: '',
      description: '',
      category: '',
      difficulty: 'Beginner',
      duration_minutes: 30,
      points_reward: 100,
      thumbnail_url: '',
      is_published: false,
    });
    setCourseDialogOpen(true);
  };

  const handleEditCourseLegacy = (course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title || '',
      description: course.description || '',
      category: course.category || '',
      difficulty: course.difficulty || 'Beginner',
      duration_minutes: course.duration_minutes || 30,
      points_reward: course.points_reward || 100,
      thumbnail_url: course.thumbnail_url || '',
      is_published: course.is_published || false,
    });
    setCourseDialogOpen(true);
  };

  const handleSaveCourse = async () => {
    if (!courseForm.title || !courseForm.category) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editingCourse) {
        await axios.put(
          `${API_URL}/admin/courses/${editingCourse.id}`,
          courseForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Course updated successfully');
      } else {
        await axios.post(`${API_URL}/admin/courses`, courseForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Course created successfully');
      }
      setCourseDialogOpen(false);
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCourse = async (courseId, courseTitle) => {
    // This function is called when user confirms deletion from AlertDialog
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/admin/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Course deleted successfully');
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete course');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (course) => {
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const handleOpenModuleDialog = (course) => {
    setSelectedCourse(course);
    setModuleForm({
      title: '',
      type: 'video',
      content: '',
      video_url: '',
      duration_minutes: 10,
      order: (course.modules?.length || 0) + 1,
    });
    setModuleDialogOpen(true);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please upload a video file');
      return;
    }

    // Check file size (max 500MB)
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Video file must be less than 500MB');
      return;
    }

    setUploadingVideo(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/video`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      setModuleForm(prev => ({
        ...prev,
        video_url: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`,
      }));
      toast.success('Video uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const validTypes = [
      'application/pdf',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ];
    const fileName = file.name.toLowerCase();
    const isValidType = validTypes.includes(file.type) || 
                        fileName.endsWith('.pdf') || 
                        fileName.endsWith('.ppt') || 
                        fileName.endsWith('.pptx');
    
    if (!isValidType) {
      toast.error('Please upload a PDF or PowerPoint file');
      return;
    }

    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Document file must be less than 50MB');
      return;
    }

    setUploadingDocument(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/core/documents/upload/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      // Use converted PDF URL if available, otherwise original URL
      const contentUrl = response.data.pdf_url || response.data.url;
      setModuleForm(prev => ({
        ...prev,
        content_url: contentUrl,
      }));
      toast.success('Document uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDocument(false);
      setUploadProgress(0);
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('audio/')) {
      toast.error('Please upload an audio file');
      return;
    }

    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Audio file must be less than 50MB');
      return;
    }

    setUploadingDocument(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/core/documents/upload/`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      setModuleForm(prev => ({
        ...prev,
        content_url: response.data.url,
      }));
      toast.success('Audio uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload audio');
    } finally {
      setUploadingDocument(false);
      setUploadProgress(0);
    }
  };

  const handleSaveModule = async () => {
    if (!moduleForm.title) {
      toast.error('Please enter module title');
      return;
    }

    if (moduleForm.type === 'video' && !moduleForm.video_url) {
      toast.error('Please upload a video or enter a video URL');
      return;
    }

    setSavingModule(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/courses/${selectedCourse.id}/modules`,
        moduleForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Module added successfully');
      setModuleDialogOpen(false);
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add module');
    } finally {
      setSavingModule(false);
    }
  };

  const handleOpenQuizDialog = (course) => {
    setSelectedCourse(course);
    // Load existing quiz data if available
    const existingQuiz = course.quiz;
    setQuizForm({
      title: existingQuiz?.title || `${course.title} Quiz`,
      passing_score: existingQuiz?.passing_score || 70,
      num_questions: 5,
      time_limit: existingQuiz?.time_limit ?? 30,
      max_attempts: existingQuiz?.max_attempts ?? 3,
      show_correct_answers: existingQuiz?.show_correct_answers ?? true,
      shuffle_questions: existingQuiz?.shuffle_questions ?? false,
      shuffle_options: existingQuiz?.shuffle_options ?? false,
      questions: existingQuiz?.questions || [],
      thumbnail_url: existingQuiz?.thumbnail_url || '',
    });
    setQuizDialogOpen(true);
  };

  const handleQuizThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingQuizThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setQuizForm(prev => ({
        ...prev,
        thumbnail_url: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`,
      }));
      toast.success('Thumbnail uploaded');
    } catch (error) {
      toast.error('Failed to upload thumbnail');
    } finally {
      setUploadingQuizThumbnail(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (!videoGenForm.prompt || !videoGenForm.title) {
      toast.error('Please fill in title and prompt');
      return;
    }

    setGeneratingVideo(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/ai/generate-video`,
        {
          title: videoGenForm.title,
          prompt: videoGenForm.prompt,
          duration: videoGenForm.duration,
          size: videoGenForm.size,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Video generated successfully!');
        setGeneratedVideos(prev => [{
          id: response.data.video_id,
          url: `${process.env.REACT_APP_BACKEND_URL}${response.data.video_url}`,
          title: videoGenForm.title,
        }, ...prev]);
        setVideoGenDialogOpen(false);
        setVideoGenForm({ title: '', prompt: '', duration: 4, size: '1280x720' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate video. This may take a while.');
    } finally {
      setGeneratingVideo(false);
    }
  };

  const handleGenerateQuiz = async () => {
    if (!selectedCourse) return;

    setGeneratingQuiz(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/ai/generate-quiz`,
        {
          course_id: selectedCourse.id,
          course_title: selectedCourse.title,
          course_description: selectedCourse.description,
          category: selectedCourse.category,
          num_questions: quizForm.num_questions,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setQuizForm(prev => ({
        ...prev,
        questions: response.data.questions || [],
      }));
      toast.success(`Generated ${response.data.questions?.length || 0} questions`);
    } catch (error) {
      toast.error('Failed to generate quiz questions');
      // Add sample questions as fallback
      setQuizForm(prev => ({
        ...prev,
        questions: [
          {
            question: `What is the main topic of ${selectedCourse.title}?`,
            type: 'multiple_choice',
            options: ['Option A', 'Option B', 'Option C', 'Option D'],
            correct_answer: 0,
          },
        ],
      }));
    } finally {
      setGeneratingQuiz(false);
    }
  };

  // Question type definitions
  const QUESTION_TYPES = [
    { id: 'multiple_choice', name: 'Multiple Choice (MCQ)', icon: 'circle-dot' },
    { id: 'multi_select', name: 'Multi-Select', icon: 'check-square' },
    { id: 'true_false', name: 'True/False', icon: 'toggle-left' },
    { id: 'match_following', name: 'Match the Following', icon: 'git-merge' },
    { id: 'image_based', name: 'Image-Based', icon: 'image' },
    { id: 'scenario', name: 'Scenario-Based', icon: 'book-open' },
  ];

  const getDefaultQuestionData = (type) => {
    switch (type) {
      case 'multiple_choice':
        return { question: '', type, options: ['', '', '', ''], correct_answer: 0, image_url: '' };
      case 'multi_select':
        return { question: '', type, options: ['', '', '', ''], correct_answers: [], image_url: '' };
      case 'true_false':
        return { question: '', type, options: ['True', 'False'], correct_answer: 0, image_url: '' };
      case 'match_following':
        return { question: '', type, left_items: ['', ''], right_items: ['', ''], correct_matches: {}, image_url: '' };
      case 'image_based':
        return { question: '', type, options: ['', '', '', ''], correct_answer: 0, image_url: '', question_image: '' };
      case 'scenario':
        return { question: '', type, scenario_text: '', options: ['', '', '', ''], correct_answer: 0, image_url: '' };
      default:
        return { question: '', type: 'multiple_choice', options: ['', '', '', ''], correct_answer: 0, image_url: '' };
    }
  };

  const handleAddManualQuestion = (questionType = 'multiple_choice') => {
    setQuizForm(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        getDefaultQuestionData(questionType),
      ],
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    setQuizForm(prev => {
      const newQuestions = [...prev.questions];
      if (field === 'option') {
        newQuestions[index].options[value.index] = value.text;
      } else if (field === 'left_item') {
        newQuestions[index].left_items[value.index] = value.text;
      } else if (field === 'right_item') {
        newQuestions[index].right_items[value.index] = value.text;
      } else if (field === 'match') {
        newQuestions[index].correct_matches[value.leftIndex] = value.rightIndex;
      } else if (field === 'toggle_correct') {
        // For multi-select
        const currentCorrect = newQuestions[index].correct_answers || [];
        if (currentCorrect.includes(value)) {
          newQuestions[index].correct_answers = currentCorrect.filter(v => v !== value);
        } else {
          newQuestions[index].correct_answers = [...currentCorrect, value];
        }
      } else if (field === 'add_option') {
        newQuestions[index].options.push('');
      } else if (field === 'remove_option') {
        newQuestions[index].options = newQuestions[index].options.filter((_, i) => i !== value);
      } else if (field === 'add_match_pair') {
        newQuestions[index].left_items.push('');
        newQuestions[index].right_items.push('');
      } else if (field === 'remove_match_pair') {
        newQuestions[index].left_items = newQuestions[index].left_items.filter((_, i) => i !== value);
        newQuestions[index].right_items = newQuestions[index].right_items.filter((_, i) => i !== value);
        delete newQuestions[index].correct_matches[value];
      } else {
        newQuestions[index][field] = value;
      }
      return { ...prev, questions: newQuestions };
    });
  };

  const handleRemoveQuestion = (index) => {
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const handleQuestionImageUpload = async (file, questionIndex, fieldName = 'question_image') => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      handleQuestionChange(questionIndex, fieldName, imageUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleSaveQuiz = async () => {
    if (quizForm.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    setSavingQuiz(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/courses/${selectedCourse.id}/quiz`,
        quizForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Quiz saved successfully');
      setQuizDialogOpen(false);
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quiz');
    } finally {
      setSavingQuiz(false);
    }
  };

  // Save Standalone Quiz - Step 1: Create Quiz
  const handleSaveStandaloneQuiz = async () => {
    if (!standaloneQuizForm.title.trim()) {
      toast.error('Please enter a quiz title');
      return;
    }
    if (standaloneQuizForm.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    setSavingQuiz(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/standalone-quizzes`,
        standaloneQuizForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Quiz created successfully! Now assign it to users.');
      setCreatedQuizId(response.data.quiz_id);
      setStandaloneQuizStep(2); // Move to assignment step
      fetchStandaloneQuizzes(); // Refresh quiz list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create quiz');
    } finally {
      setSavingQuiz(false);
    }
  };

  // Assign Standalone Quiz - Step 2
  const handleAssignStandaloneQuiz = async () => {
    setAssigning(true);
    try {
      const token = localStorage.getItem('token');
      
      // First, update the quiz with publish status
      if (standaloneQuizForm.is_published) {
        await axios.post(
          `${API_URL}/standalone-quizzes/${createdQuizId}/publish`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Then assign if roles are selected
      if (standaloneQuizAssignment.roles.length > 0) {
        await axios.post(
          `${API_URL}/standalone-quizzes/${createdQuizId}/assign`,
          {
            ...standaloneQuizAssignment,
            assign_to: 'role'  // Always use role since we redesigned the UI
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Quiz published and assigned successfully!');
      } else {
        toast.success(standaloneQuizForm.is_published ? 'Quiz published successfully!' : 'Quiz saved successfully!');
      }
      
      fetchStandaloneQuizzes();
      resetStandaloneQuizDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete quiz setup');
    } finally {
      setAssigning(false);
    }
  };

  // Reset standalone quiz dialog
  const resetStandaloneQuizDialog = () => {
    setStandaloneQuizDialogOpen(false);
    setStandaloneQuizStep(1);
    setCreatedQuizId(null);
    setStandaloneQuizForm({
      title: '',
      description: '',
      category: 'Assessment',
      passing_score: 70,
      num_questions: 5,
      time_limit: 30,
      max_attempts: 3,
      show_correct_answers: true,
      shuffle_questions: false,
      shuffle_options: false,
      questions: [],
      thumbnail_url: '',
      is_published: false,
    });
    setStandaloneQuizAssignment({
      assign_to: 'role',
      roles: [],
      user_ids: [],
      due_date: '',
      skip_assignment: false,
    });
  };

  // Handle dialog close with confirmation
  const handleDialogClose = (dialogType, isOpen) => {
    if (!isOpen) {
      // User is trying to close
      if (dialogType === 'standalone-quiz' && (standaloneQuizForm.title || standaloneQuizForm.questions.length > 0)) {
        setConfirmCloseDialog({ open: true, type: 'standalone-quiz' });
        return;
      }
      if (dialogType === 'course-wizard' && wizardOpen) {
        setConfirmCloseDialog({ open: true, type: 'course-wizard' });
        return;
      }
      if (dialogType === 'quiz' && (quizForm.title || quizForm.questions.length > 0)) {
        setConfirmCloseDialog({ open: true, type: 'quiz' });
        return;
      }
    }
    // Allow open or close when no unsaved data
    if (dialogType === 'standalone-quiz') setStandaloneQuizDialogOpen(isOpen);
    if (dialogType === 'course-wizard') setWizardOpen(isOpen);
    if (dialogType === 'quiz') setQuizDialogOpen(isOpen);
  };

  const confirmDialogClose = () => {
    if (confirmCloseDialog.type === 'standalone-quiz') {
      resetStandaloneQuizDialog();
    }
    if (confirmCloseDialog.type === 'course-wizard') {
      setWizardOpen(false);
    }
    if (confirmCloseDialog.type === 'quiz') {
      setQuizDialogOpen(false);
      setQuizForm({
        title: '',
        passing_score: 70,
        num_questions: 5,
        time_limit: 30,
        max_attempts: 3,
        show_correct_answers: true,
        shuffle_questions: false,
        shuffle_options: false,
        questions: [],
        thumbnail_url: '',
      });
    }
    setConfirmCloseDialog({ open: false, type: null });
  };

  // Add question to standalone quiz
  const handleAddStandaloneQuestion = (questionType = 'single_choice') => {
    let newQuestion = {
      id: `q_${Date.now()}`,
      question: '',
      type: questionType,
      correct_answer: 0,
      explanation: '',
    };

    // Set options based on question type
    if (questionType === 'single_choice' || questionType === 'multiple_choice') {
      newQuestion.options = ['', '', '', ''];
    } else if (questionType === 'true_false') {
      newQuestion.options = ['True', 'False'];
    } else if (questionType === 'match_following') {
      newQuestion.pairs = [
        { left: '', right: '' },
        { left: '', right: '' }
      ];
    }

    setStandaloneQuizForm(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  // Update standalone quiz question
  const handleUpdateStandaloneQuestion = (index, field, value) => {
    setStandaloneQuizForm(prev => {
      const newQuestions = [...prev.questions];
      if (field === 'options') {
        newQuestions[index].options = value;
      } else {
        newQuestions[index][field] = value;
      }
      return { ...prev, questions: newQuestions };
    });
  };

  // Remove standalone quiz question
  const handleRemoveStandaloneQuestion = (index) => {
    setStandaloneQuizForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleOpenAssignDialog = (course) => {
    setSelectedCourse(course);
    setAssignmentForm({
      assign_to: 'role',
      roles: [], // Reset to empty array
      user_ids: [],
      due_date: '',
    });
    setRoleSelectOpen(false);
    setAssignDialogOpen(true);
  };

  // Available roles for assignment - synced with User Management
  const ACTUAL_ROLES = [
    { value: 'admin', label: 'Administrators' },
    { value: 'training_team', label: 'L&D Admins' },
    { value: 'regional_manager', label: 'Regional Managers' },
    { value: 'store_manager', label: 'Store Managers' },
    { value: 'cra', label: 'Store Executives' },
  ];

  const ALL_ROLE_VALUES = ACTUAL_ROLES.map(r => r.value);

  const toggleRole = (roleValue) => {
    if (roleValue === 'all') {
      // "All Learners" toggles all roles
      setAssignmentForm(prev => {
        const allSelected = ALL_ROLE_VALUES.every(r => prev.roles.includes(r));
        return {
          ...prev,
          roles: allSelected ? [] : [...ALL_ROLE_VALUES]
        };
      });
    } else {
      setAssignmentForm(prev => {
        const isSelected = prev.roles.includes(roleValue);
        return {
          ...prev,
          roles: isSelected
            ? prev.roles.filter(r => r !== roleValue)
            : [...prev.roles, roleValue]
        };
      });
    }
  };

  const isAllSelected = ALL_ROLE_VALUES.every(r => assignmentForm.roles.includes(r));

  const getSelectedUsersCount = () => {
    if (assignmentForm.roles.length === 0) return 0;
    return users.filter(u => assignmentForm.roles.includes(u.role)).length;
  };

  const handleAssignCourse = async () => {
    if (assignmentForm.assign_to === 'role' && assignmentForm.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    if (assignmentForm.assign_to === 'users' && assignmentForm.user_ids.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    if (!assignmentForm.due_date) {
      toast.error('Please select a due date');
      return;
    }

    setAssigning(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/admin/courses/${selectedCourse.id}/assign`,
        assignmentForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Course assigned successfully! Notifications sent to users.');
      setAssignDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign course');
    } finally {
      setAssigning(false);
    }
  };

  // Fetch course enrollment statistics
  const fetchCourseStats = async (course) => {
    setSelectedCourse(course);
    setLoadingStats(true);
    setStatsDialogOpen(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/admin/courses/${course.id}/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCourseStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch course statistics');
      setCourseStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const filteredCourses = courses.filter(c => {
    const matchesSearch = c.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || c.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Combine courses and published standalone quizzes for display
  const publishedQuizzesAsContent = standaloneQuizzes
    .filter(q => q.is_published)
    .map(q => ({
      ...q,
      _isQuiz: true,
      modules: [],
      duration_minutes: q.time_limit || 15,
      points_reward: 50
    }));
  
  const filteredContent = [...filteredCourses, ...publishedQuizzesAsContent.filter(q => {
    const matchesSearch = q.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || q.category === selectedCategory;
    return matchesSearch && matchesCategory;
  })];

  // Apply sorting
  const allContent = [...filteredContent].sort((a, b) => {
    switch (sortBy) {
      case 'latest_created':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'oldest_created':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      case 'latest_assigned':
        return new Date(b.assigned_at || b.created_at || 0) - new Date(a.assigned_at || a.created_at || 0);
      case 'name_asc':
        return (a.title || '').localeCompare(b.title || '');
      case 'name_desc':
        return (b.title || '').localeCompare(a.title || '');
      default:
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
  });

  // Fetch quiz analytics
  const fetchQuizAnalytics = async (quizId) => {
    setLoadingAnalytics(true);
    setQuizAnalyticsOpen(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/standalone-quizzes/${quizId}/analytics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuizAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch quiz analytics');
      setQuizAnalytics(null);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'training_team') {
    return (
      <DashboardLayout>
        <Card className="p-12 text-center">
          <XCircle className="w-16 h-16 mx-auto text-destructive mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">Only administrators and training team can access this panel.</p>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 lg:pb-0" data-testid="course-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <GraduationCap className="w-8 h-8 text-primary" />
              {t('management.courseManagement', 'Course Management')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('management.courseManagementSubtitle', 'Create courses, upload content, and generate AI-powered quizzes')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUseAICourseGen && (
              <Button 
                variant="outline" 
                onClick={() => setAiCourseDialogOpen(true)} 
                data-testid="generate-ai-course-btn"
                className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/30 hover:border-purple-500"
              >
                <Brain className="w-4 h-4 mr-2 text-purple-500" />
                {t('management.generateAICourse')}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="create-dropdown-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('management.create')}
                  <ChevronsUpDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  onClick={handleCreateCourse}
                  data-testid="create-course-btn"
                  className="cursor-pointer"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  {t('courses.createCourse', 'Create Course')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => {
                    setStandaloneQuizForm({
                      title: '',
                      description: '',
                      category: 'Assessment',
                      passing_score: 70,
                      num_questions: 5,
                      time_limit: 30,
                      max_attempts: 3,
                      show_correct_answers: true,
                      shuffle_questions: false,
                      shuffle_options: false,
                      questions: [],
                      thumbnail_url: '',
                      is_published: false,
                    });
                    setStandaloneQuizDialogOpen(true);
                  }}
                  data-testid="create-quiz-btn"
                  className="cursor-pointer"
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  {t('courses.createQuiz', 'Create Quiz')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <BookOpen className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{courses.length}</p>
                  <p className="text-xs text-muted-foreground">{t('management.totalCourses', 'Total Courses')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{courses.filter(c => c.is_published).length}</p>
                  <p className="text-xs text-muted-foreground">{t('management.published', 'Published')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Video className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{courses.reduce((acc, c) => acc + (c.modules?.length || 0), 0)}</p>
                  <p className="text-xs text-muted-foreground">{t('management.totalModules', 'Total Modules')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <HelpCircle className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{courses.filter(c => c.quiz).length}</p>
                  <p className="text-xs text-muted-foreground">{t('management.withQuizzes')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('management.searchCourses')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-courses"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full md:w-48">
              <SelectValue placeholder={t('management.filterByCategory')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('management.allCategories')}</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-full md:w-48" data-testid="sort-select">
              <SelectValue placeholder={t('common.sort')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest_created">{t('management.latestCreated')}</SelectItem>
              <SelectItem value="oldest_created">{t('management.oldestFirst')}</SelectItem>
              <SelectItem value="latest_assigned">{t('management.latestAssigned', 'Latest Assigned')}</SelectItem>
              <SelectItem value="name_asc">{t('common.nameAZ', 'Name (A-Z)')}</SelectItem>
              <SelectItem value="name_desc">{t('common.nameZA', 'Name (Z-A)')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Courses Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : allContent.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allContent.map((item) => (
              <Card key={item.id} className="overflow-hidden" data-testid={item._isQuiz ? `quiz-card-${item.id}` : `course-card-${item.id}`}>
                <div className={`h-32 flex items-center justify-center ${item._isQuiz ? 'bg-gradient-to-br from-purple-500/20 to-purple-500/5' : 'bg-gradient-to-br from-primary/20 to-primary/5'}`}>
                  {item.thumbnail_url ? (
                    <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover" />
                  ) : item._isQuiz ? (
                    <HelpCircle className="w-12 h-12 text-purple-500/40" />
                  ) : (
                    <BookOpen className="w-12 h-12 text-primary/40" />
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{item.category}</Badge>
                      {item._isQuiz && <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">{t('courses.quiz', 'Quiz')}</Badge>}
                    </div>
                    <Badge variant={item.is_published ? 'default' : 'secondary'}>
                      {item.is_published ? t('management.published') : t('management.draft')}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-lg mb-1 line-clamp-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{item.description}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {item.duration_minutes || 30} {t('common.minutes')}
                    </span>
                    {item._isQuiz ? (
                      <span className="flex items-center gap-1">
                        <HelpCircle className="w-3 h-3" />
                        {item.questions?.length || 0} questions
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Video className="w-3 h-3" />
                        {item.modules?.length || 0} modules
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {item.points_reward || 100} pts
                    </span>
                  </div>
                  <div className={item._isQuiz ? "flex flex-wrap gap-2" : "flex flex-col gap-2"}>
                    {item._isQuiz ? (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setStandaloneQuizForm({ ...item, id: item.id });
                            setStandaloneQuizDialogOpen(true);
                          }}
                          data-testid={`edit-quiz-${item.id}`}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          {t('common.edit')}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => {
                            setCreatedQuizId(item.id);
                            setStandaloneQuizStep(2);
                            setStandaloneQuizForm({ ...item, id: item.id });
                            setStandaloneQuizDialogOpen(true);
                          }}
                          data-testid={`assign-quiz-${item.id}`}
                        >
                          <Users className="w-3 h-3 mr-1" />
                          {t('management.assign')}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => fetchQuizAnalytics(item.id)}
                          data-testid={`quiz-analytics-${item.id}`}
                          className="text-purple-600 border-purple-500 hover:bg-purple-500/10"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          {t('management.stats')}
                        </Button>
                      </>
                    ) : (
                      <>
                        {/* Row 1: Preview and Edit Flow */}
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => navigate(`/course-preview/${item.id}`)}
                            className="flex-1 text-amber-600 border-amber-500 hover:bg-amber-500/10"
                            data-testid={`preview-course-${item.id}`}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            {t('courses.preview', 'Preview')}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleEditCourse(item)}
                            className="flex-1"
                            data-testid={`edit-course-${item.id}`}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            {t('courses.editFlow', 'Edit Flow')}
                          </Button>
                        </div>
                        {/* Row 2: Stats and Delete (for drafts) */}
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => fetchCourseStats(item)}
                            className={user?.role === 'admin' && !item.is_published ? "flex-1" : "flex-1"}
                            data-testid={`course-stats-${item.id}`}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            {t('courses.stats', 'Stats')}
                          </Button>
                          {/* Delete button for draft courses - Admin only */}
                          {user?.role === 'admin' && !item.is_published && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openDeleteDialog(item)}
                              className="flex-1 text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                              data-testid={`delete-course-${item.id}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              {t('common.delete')}
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Show translation badges for courses */}
                  {!item._isQuiz && item.translations && Object.keys(item.translations).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <Badge variant="secondary" className="text-xs">
                        {Object.keys(item.translations).length} {t('courses.translations', 'translations')}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">{t('management.noCoursesFound', 'No courses or quizzes found')}</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || selectedCategory !== 'all'
                ? t('management.tryAdjusting', 'Try adjusting your search or filters')
                : t('management.createFirst', 'Create your first course or quiz to get started')}
            </p>
            <Button onClick={handleCreateCourse}>
              <Plus className="w-4 h-4 mr-2" />
              Create Course
            </Button>
          </Card>
        )}

        {/* Standalone Quizzes Section - Draft Quizzes */}
        {standaloneQuizzes.filter(q => !q.is_published).length > 0 && (
          <Card className="mt-6">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-purple-500" />
                  <CardTitle className="text-lg">{t('management.draftQuizzes', 'Draft Quizzes')}</CardTitle>
                </div>
                <Badge variant="secondary">{standaloneQuizzes.filter(q => !q.is_published).length} {t('management.drafts', 'drafts')}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left py-3 px-4 font-medium">{t('common.title')}</th>
                      <th className="text-left py-3 px-4 font-medium hidden md:table-cell">{t('common.category')}</th>
                      <th className="text-center py-3 px-4 font-medium hidden sm:table-cell">{t('quiz.questions')}</th>
                      <th className="text-center py-3 px-4 font-medium hidden md:table-cell">{t('quiz.passingScore')}</th>
                      <th className="text-center py-3 px-4 font-medium">{t('common.status')}</th>
                      <th className="text-right py-3 px-4 font-medium">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {standaloneQuizzes.filter(q => !q.is_published).map((quiz) => (
                      <tr key={quiz.id} className="hover:bg-muted/30" data-testid={`standalone-quiz-row-${quiz.id}`}>
                        <td className="py-3 px-4">
                          <div className="font-medium">{quiz.title}</div>
                          <div className="text-xs text-muted-foreground md:hidden">{quiz.category}</div>
                        </td>
                        <td className="py-3 px-4 hidden md:table-cell">
                          <Badge variant="outline">{quiz.category}</Badge>
                        </td>
                        <td className="py-3 px-4 text-center hidden sm:table-cell">
                          {quiz.questions?.length || 0}
                        </td>
                        <td className="py-3 px-4 text-center hidden md:table-cell">
                          {quiz.passing_score}%
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge variant={quiz.is_published ? "default" : "secondary"} className={quiz.is_published ? "bg-green-600" : ""}>
                            {quiz.is_published ? 'Published' : 'Draft'}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => {
                                  setStandaloneQuizForm({
                                    ...quiz,
                                    id: quiz.id,
                                  });
                                  setStandaloneQuizDialogOpen(true);
                                }}
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Quiz
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.post(
                                      `${API_URL}/standalone-quizzes/${quiz.id}/publish`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success('Quiz published successfully');
                                    fetchStandaloneQuizzes();
                                  } catch (error) {
                                    toast.error('Failed to publish quiz');
                                  }
                                }}
                                disabled={quiz.is_published}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Publish
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setCreatedQuizId(quiz.id);
                                  setStandaloneQuizStep(2);
                                  setStandaloneQuizForm({ ...quiz, id: quiz.id });
                                  setStandaloneQuizDialogOpen(true);
                                }}
                              >
                                <Users className="w-4 h-4 mr-2" />
                                Assign Users
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={async () => {
                                  if (window.confirm(`Are you sure you want to delete "${quiz.title}"?`)) {
                                    try {
                                      const token = localStorage.getItem('token');
                                      await axios.delete(
                                        `${API_URL}/standalone-quizzes/${quiz.id}`,
                                        { headers: { Authorization: `Bearer ${token}` } }
                                      );
                                      toast.success('Quiz deleted successfully');
                                      fetchStandaloneQuizzes();
                                    } catch (error) {
                                      toast.error('Failed to delete quiz');
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Course Wizard */}
        <CourseWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          editingCourse={wizardEditingCourse}
          onSaveComplete={fetchCourses}
          users={users}
        />

        {/* Legacy Course Dialog - Hidden, kept for backward compatibility */}
        <Dialog open={courseDialogOpen} onOpenChange={setCourseDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCourse ? 'Edit Course' : 'Create New Course'}</DialogTitle>
              <DialogDescription>
                Fill in the course details. You can add modules and quizzes after creating the course.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="title">Course Title *</Label>
                  <Input
                    id="title"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Introduction to Sales Techniques"
                    data-testid="course-title"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={courseForm.description}
                    onChange={(e) => setCourseForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what learners will gain from this course..."
                    rows={3}
                    data-testid="course-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={courseForm.category}
                    onValueChange={(value) => setCourseForm(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger data-testid="course-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Difficulty Level</Label>
                  <Select
                    value={courseForm.difficulty}
                    onValueChange={(value) => setCourseForm(prev => ({ ...prev, difficulty: value }))}
                  >
                    <SelectTrigger data-testid="course-difficulty">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DIFFICULTY_LEVELS.map(level => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={courseForm.duration_minutes}
                    onChange={(e) => setCourseForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))}
                    min={5}
                    data-testid="course-duration"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="points">Points Reward</Label>
                  <Input
                    id="points"
                    type="number"
                    value={courseForm.points_reward}
                    onChange={(e) => setCourseForm(prev => ({ ...prev, points_reward: parseInt(e.target.value) || 100 }))}
                    min={0}
                    data-testid="course-points"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="thumbnail">Thumbnail URL</Label>
                  <Input
                    id="thumbnail"
                    value={courseForm.thumbnail_url}
                    onChange={(e) => setCourseForm(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                    placeholder="https://example.com/image.jpg"
                    data-testid="course-thumbnail"
                  />
                </div>
                <div className="col-span-2 flex items-center justify-between">
                  <div>
                    <Label>Publish Course</Label>
                    <p className="text-xs text-muted-foreground">Make this course visible to learners</p>
                  </div>
                  <Switch
                    checked={courseForm.is_published}
                    onCheckedChange={(checked) => setCourseForm(prev => ({ ...prev, is_published: checked }))}
                    data-testid="course-publish"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCourseDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveCourse} disabled={saving} data-testid="save-course-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                {editingCourse ? 'Update Course' : 'Create Course'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Module Dialog */}
        <Dialog open={moduleDialogOpen && !slideEditorOpen} onOpenChange={setModuleDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Module to: {selectedCourse?.title}</DialogTitle>
              <DialogDescription>
                Add a video, text content, or external link as a course module
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="module-title">Module Title *</Label>
                <Input
                  id="module-title"
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Introduction Video"
                  data-testid="module-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select
                  value={moduleForm.type}
                  onValueChange={(value) => setModuleForm(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger data-testid="module-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4" /> Video
                      </div>
                    </SelectItem>
                    <SelectItem value="text">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Text Content
                      </div>
                    </SelectItem>
                    <SelectItem value="slide">
                      <div className="flex items-center gap-2">
                        <Presentation className="w-4 h-4" /> Slide Designer
                      </div>
                    </SelectItem>
                    <SelectItem value="link">
                      <div className="flex items-center gap-2">
                        <LinkIcon className="w-4 h-4" /> External Link
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {moduleForm.type === 'video' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Upload Video</Label>
                    <div className="border-2 border-dashed rounded-lg p-6 text-center">
                      {uploadingVideo ? (
                        <div className="space-y-2">
                          <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                          <Progress value={uploadProgress} className="w-full" />
                          <p className="text-sm text-muted-foreground">Uploading... {uploadProgress}%</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                          <p className="text-sm text-muted-foreground mb-2">
                            Drag and drop or click to upload
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => videoInputRef.current?.click()}
                          >
                            Select Video
                          </Button>
                          <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            ref={videoInputRef}
                            onChange={handleVideoUpload}
                          />
                          <p className="text-xs text-muted-foreground mt-2">Max file size: 500MB</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <Separator className="my-2" />
                    <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">OR</span>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="video-url">Video URL (YouTube, Vimeo, etc.)</Label>
                    <Input
                      id="video-url"
                      value={moduleForm.video_url}
                      onChange={(e) => setModuleForm(prev => ({ ...prev, video_url: e.target.value }))}
                      placeholder="https://youtube.com/watch?v=..."
                      data-testid="module-video-url"
                    />
                  </div>
                </div>
              )}

              {moduleForm.type === 'text' && (
                <div className="space-y-2">
                  <Label htmlFor="module-content">Text Content</Label>
                  <Textarea
                    id="module-content"
                    value={moduleForm.content}
                    onChange={(e) => setModuleForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Enter the text content for this module..."
                    rows={6}
                    data-testid="module-content"
                  />
                </div>
              )}

              {moduleForm.type === 'slide' && (
                <div className="space-y-2">
                  <Label>Slide Content</Label>
                  <div className="border rounded-lg p-4 bg-muted/20 text-center space-y-2">
                    {moduleForm.content ? (
                      <p className="text-sm text-muted-foreground">
                        {(() => { try { const d = JSON.parse(moduleForm.content); return `${d.slides?.length || 0} slide(s) designed`; } catch { return 'Slide data saved'; } })()}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">No slides designed yet</p>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSlideEditorOpen(true)}
                      data-testid="open-slide-designer"
                    >
                      <Presentation className="w-4 h-4 mr-2" />
                      {moduleForm.content ? 'Edit Slides' : 'Open Slide Designer'}
                    </Button>
                  </div>
                </div>
              )}

              {moduleForm.type === 'link' && (
                <div className="space-y-2">
                  <Label htmlFor="module-link">External Link</Label>
                  <Input
                    id="module-link"
                    value={moduleForm.content}
                    onChange={(e) => setModuleForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="https://example.com/resource"
                    data-testid="module-link"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="module-duration">Duration (minutes)</Label>
                <Input
                  id="module-duration"
                  type="number"
                  value={moduleForm.duration_minutes}
                  onChange={(e) => setModuleForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 10 }))}
                  min={1}
                  data-testid="module-duration"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModuleDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveModule} disabled={savingModule} data-testid="save-module-btn">
                {savingModule ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Module
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quiz Dialog */}
        <Dialog open={quizDialogOpen} onOpenChange={setQuizDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5" />
                Quiz Builder: {selectedCourse?.title}
              </DialogTitle>
              <DialogDescription>
                Generate AI-powered questions or add them manually
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quiz Title</Label>
                  <Input
                    value={quizForm.title}
                    onChange={(e) => setQuizForm(prev => ({ ...prev, title: e.target.value }))}
                    data-testid="quiz-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Passing Score (%)</Label>
                  <Input
                    type="number"
                    value={quizForm.passing_score}
                    onChange={(e) => setQuizForm(prev => ({ ...prev, passing_score: parseInt(e.target.value) || 70 }))}
                    min={0}
                    max={100}
                    data-testid="quiz-passing-score"
                  />
                </div>
              </div>

              {/* Quiz Configuration */}
              <Card className="border-blue-500/20 bg-blue-500/5">
                <CardContent className="p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Quiz Configuration
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Time Limit (minutes)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={quizForm.time_limit}
                          onChange={(e) => setQuizForm(prev => ({ ...prev, time_limit: parseInt(e.target.value) || 0 }))}
                          min={0}
                          max={180}
                          className="flex-1"
                          data-testid="quiz-time-limit"
                        />
                        <span className="text-xs text-muted-foreground">0 = No limit</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Max Attempts</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={quizForm.max_attempts}
                          onChange={(e) => setQuizForm(prev => ({ ...prev, max_attempts: parseInt(e.target.value) || 0 }))}
                          min={0}
                          max={10}
                          className="flex-1"
                          data-testid="quiz-max-attempts"
                        />
                        <span className="text-xs text-muted-foreground">0 = Unlimited</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-2">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="show-answers"
                        checked={quizForm.show_correct_answers}
                        onCheckedChange={(checked) => setQuizForm(prev => ({ ...prev, show_correct_answers: checked }))}
                      />
                      <Label htmlFor="show-answers" className="text-sm">Show correct answers after submission</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="shuffle-questions"
                        checked={quizForm.shuffle_questions}
                        onCheckedChange={(checked) => setQuizForm(prev => ({ ...prev, shuffle_questions: checked }))}
                      />
                      <Label htmlFor="shuffle-questions" className="text-sm">Shuffle questions</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="shuffle-options"
                        checked={quizForm.shuffle_options}
                        onCheckedChange={(checked) => setQuizForm(prev => ({ ...prev, shuffle_options: checked }))}
                      />
                      <Label htmlFor="shuffle-options" className="text-sm">Shuffle options</Label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quiz Thumbnail Upload */}
              <div className="space-y-2">
                <Label>Quiz Thumbnail (optional)</Label>
                <div className="flex items-center gap-4">
                  <div 
                    className="w-32 h-20 rounded-lg border-2 border-dashed border-muted-foreground/50 flex items-center justify-center cursor-pointer hover:border-primary transition-colors overflow-hidden"
                    onClick={() => quizThumbnailRef.current?.click()}
                  >
                    {quizForm.thumbnail_url ? (
                      <img src={quizForm.thumbnail_url} alt="Quiz thumbnail" className="w-full h-full object-cover" />
                    ) : uploadingQuizThumbnail ? (
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    ) : (
                      <Image className="w-6 h-6 text-muted-foreground" />
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    ref={quizThumbnailRef}
                    onChange={handleQuizThumbnailUpload}
                  />
                  <div className="text-sm text-muted-foreground">
                    <p>Click to upload thumbnail</p>
                    <p className="text-xs">Recommended: 640x400px</p>
                  </div>
                </div>
              </div>

              {/* AI Generation - Only show if AI features are enabled */}
              {canUseAIQuizGen && (
                <Card className="bg-gradient-to-r from-purple-500/10 to-primary/10 border-purple-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-purple-500/20">
                          <Sparkles className="w-5 h-5 text-purple-500" />
                        </div>
                        <div>
                          <h4 className="font-medium">AI Quiz Generator</h4>
                          <p className="text-sm text-muted-foreground">Generate questions based on course content</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select
                          value={quizForm.num_questions.toString()}
                          onValueChange={(v) => setQuizForm(prev => ({ ...prev, num_questions: parseInt(v) }))}
                        >
                          <SelectTrigger className="w-24" data-testid="quiz-num-questions">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[3, 5, 7, 10, 15, 20].map(n => (
                              <SelectItem key={n} value={n.toString()}>{n} Qs</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button onClick={handleGenerateQuiz} disabled={generatingQuiz} data-testid="generate-quiz-btn">
                          {generatingQuiz ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-2" />
                          )}
                          Generate
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Separator />

              {/* Questions List */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Questions ({quizForm.questions.length})</h4>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="add-question-btn">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Question
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => handleAddManualQuestion('multiple_choice')}>
                        <CircleDot className="w-4 h-4 mr-2" />
                        Multiple Choice (MCQ)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAddManualQuestion('multi_select')}>
                        <CheckSquare className="w-4 h-4 mr-2" />
                        Multi-Select
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAddManualQuestion('true_false')}>
                        <ToggleLeft className="w-4 h-4 mr-2" />
                        True/False
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAddManualQuestion('match_following')}>
                        <GitMerge className="w-4 h-4 mr-2" />
                        Match the Following
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAddManualQuestion('image_based')}>
                        <ImagePlus className="w-4 h-4 mr-2" />
                        Image-Based
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleAddManualQuestion('scenario')}>
                        <BookOpen className="w-4 h-4 mr-2" />
                        Scenario-Based
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {quizForm.questions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <HelpCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No questions yet. Generate with AI or add manually.</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-4 pr-4">
                      {quizForm.questions.map((q, qIndex) => (
                        <Card key={qIndex} className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">Q{qIndex + 1}</Badge>
                              <Badge variant="secondary" className="capitalize">
                                {q.type?.replace('_', ' ') || 'MCQ'}
                              </Badge>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive"
                              onClick={() => handleRemoveQuestion(qIndex)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Question Text */}
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs">Question Text</Label>
                              <Textarea
                                value={q.question}
                                onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}
                                placeholder="Enter question..."
                                rows={2}
                              />
                            </div>

                            {/* Scenario Text (for scenario-based questions) */}
                            {q.type === 'scenario' && (
                              <div>
                                <Label className="text-xs">Scenario Description</Label>
                                <Textarea
                                  value={q.scenario_text || ''}
                                  onChange={(e) => handleQuestionChange(qIndex, 'scenario_text', e.target.value)}
                                  placeholder="Describe the scenario..."
                                  rows={3}
                                />
                              </div>
                            )}

                            {/* Image Upload (for image-based questions) */}
                            {(q.type === 'image_based' || q.type === 'scenario') && (
                              <div>
                                <Label className="text-xs">Question Image</Label>
                                <div className="flex items-center gap-2 mt-1">
                                  {q.question_image ? (
                                    <div className="relative">
                                      <img src={q.question_image} alt="Question" className="h-20 w-32 object-cover rounded border" />
                                      <Button
                                        variant="destructive"
                                        size="icon"
                                        className="absolute -top-2 -right-2 h-6 w-6"
                                        onClick={() => handleQuestionChange(qIndex, 'question_image', '')}
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <label className="flex items-center gap-2 px-4 py-2 border border-dashed rounded cursor-pointer hover:bg-muted">
                                      <ImagePlus className="w-4 h-4" />
                                      <span className="text-sm">Upload Image</span>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => e.target.files[0] && handleQuestionImageUpload(e.target.files[0], qIndex, 'question_image')}
                                      />
                                    </label>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* MCQ / Multi-Select / True-False / Image-Based / Scenario Options */}
                            {['multiple_choice', 'multi_select', 'true_false', 'image_based', 'scenario'].includes(q.type) && (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs">
                                    {q.type === 'multi_select' ? 'Options (select all correct)' : 'Options (select correct answer)'}
                                  </Label>
                                  {q.type !== 'true_false' && (
                                    <Button variant="ghost" size="sm" onClick={() => handleQuestionChange(qIndex, 'add_option', null)}>
                                      <Plus className="w-3 h-3 mr-1" />
                                      Add
                                    </Button>
                                  )}
                                </div>
                                {q.options?.map((opt, optIndex) => (
                                  <div key={optIndex} className="flex items-center gap-2">
                                    {q.type === 'multi_select' ? (
                                      <Checkbox
                                        checked={(q.correct_answers || []).includes(optIndex)}
                                        onCheckedChange={() => handleQuestionChange(qIndex, 'toggle_correct', optIndex)}
                                      />
                                    ) : (
                                      <input
                                        type="radio"
                                        name={`correct-${qIndex}`}
                                        checked={q.correct_answer === optIndex}
                                        onChange={() => handleQuestionChange(qIndex, 'correct_answer', optIndex)}
                                        className="w-4 h-4"
                                      />
                                    )}
                                    <Input
                                      value={opt}
                                      onChange={(e) => handleQuestionChange(qIndex, 'option', { index: optIndex, text: e.target.value })}
                                      placeholder={q.type === 'true_false' ? opt : `Option ${String.fromCharCode(65 + optIndex)}`}
                                      className="flex-1"
                                      disabled={q.type === 'true_false'}
                                    />
                                    {q.type !== 'true_false' && q.options.length > 2 && (
                                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleQuestionChange(qIndex, 'remove_option', optIndex)}>
                                        <Minus className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Match the Following */}
                            {q.type === 'match_following' && (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <Label className="text-xs">Match Pairs (drag to connect)</Label>
                                  <Button variant="ghost" size="sm" onClick={() => handleQuestionChange(qIndex, 'add_match_pair', null)}>
                                    <Plus className="w-3 h-3 mr-1" />
                                    Add Pair
                                  </Button>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-2">
                                    <span className="text-xs font-medium">Left Column</span>
                                    {q.left_items?.map((item, idx) => (
                                      <div key={idx} className="flex items-center gap-1">
                                        <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0">{idx + 1}</Badge>
                                        <Input
                                          value={item}
                                          onChange={(e) => handleQuestionChange(qIndex, 'left_item', { index: idx, text: e.target.value })}
                                          placeholder={`Item ${idx + 1}`}
                                          className="flex-1 h-8"
                                        />
                                      </div>
                                    ))}
                                  </div>
                                  <div className="space-y-2">
                                    <span className="text-xs font-medium">Right Column (Matches)</span>
                                    {q.right_items?.map((item, idx) => (
                                      <div key={idx} className="flex items-center gap-1">
                                        <Select
                                          value={String(q.correct_matches?.[idx] ?? '')}
                                          onValueChange={(v) => handleQuestionChange(qIndex, 'match', { leftIndex: idx, rightIndex: parseInt(v) })}
                                        >
                                          <SelectTrigger className="w-12 h-8 p-1">
                                            <SelectValue placeholder="-" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {q.left_items?.map((_, i) => (
                                              <SelectItem key={i} value={String(i)}>{i + 1}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Input
                                          value={item}
                                          onChange={(e) => handleQuestionChange(qIndex, 'right_item', { index: idx, text: e.target.value })}
                                          placeholder={`Match ${idx + 1}`}
                                          className="flex-1 h-8"
                                        />
                                        {q.left_items?.length > 2 && (
                                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleQuestionChange(qIndex, 'remove_match_pair', idx)}>
                                            <Minus className="w-3 h-3" />
                                          </Button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setQuizDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveQuiz} disabled={savingQuiz || quizForm.questions.length === 0} data-testid="save-quiz-btn">
                {savingQuiz ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Save Quiz
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assignment Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Assign Course: {selectedCourse?.title}
              </DialogTitle>
              <DialogDescription>
                Assign this course to specific users or entire roles
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select
                  value={assignmentForm.assign_to}
                  onValueChange={(value) => setAssignmentForm(prev => ({ ...prev, assign_to: value }))}
                >
                  <SelectTrigger data-testid="assign-to">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="role">Entire Role/Group</SelectItem>
                    <SelectItem value="users">Specific Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {assignmentForm.assign_to === 'role' && (
                <div className="space-y-2">
                  <Label>Select Roles</Label>
                  <Popover open={roleSelectOpen} onOpenChange={setRoleSelectOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={roleSelectOpen}
                        className="w-full justify-between h-auto min-h-10"
                        data-testid="assign-role-multiselect"
                      >
                        {assignmentForm.roles.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {isAllSelected ? (
                              <Badge variant="secondary" className="mr-1 mb-1">
                                All Learners
                                <button
                                  className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleRole('all');
                                  }}
                                >
                                  <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                </button>
                              </Badge>
                            ) : (
                              assignmentForm.roles.map(roleValue => {
                                const role = ACTUAL_ROLES.find(r => r.value === roleValue);
                                return (
                                  <Badge
                                    key={roleValue}
                                    variant="secondary"
                                    className="mr-1 mb-1"
                                  >
                                    {role?.label || roleValue}
                                    <button
                                      className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          toggleRole(roleValue);
                                        }
                                      }}
                                      onMouseDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleRole(roleValue);
                                      }}
                                    >
                                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                    </button>
                                  </Badge>
                                );
                              })
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Select roles...</span>
                        )}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search roles..." />
                        <CommandList>
                          <CommandEmpty>No role found.</CommandEmpty>
                          <CommandGroup>
                            {/* All Learners - Select All option */}
                            <CommandItem
                              value="all"
                              onSelect={() => toggleRole('all')}
                              className="font-medium border-b mb-1"
                            >
                              <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary ${isAllSelected ? 'bg-primary text-primary-foreground' : 'opacity-50'}`}>
                                {isAllSelected && <Check className="h-3 w-3" />}
                              </div>
                              <span className="flex-1">All Learners</span>
                              <span className="text-xs text-muted-foreground">
                                ({users.length} users)
                              </span>
                            </CommandItem>
                            {/* Individual roles */}
                            {ACTUAL_ROLES.map((role) => {
                              const isSelected = assignmentForm.roles.includes(role.value);
                              const roleUserCount = users.filter(u => u.role === role.value).length;
                              return (
                                <CommandItem
                                  key={role.value}
                                  value={role.value}
                                  onSelect={() => toggleRole(role.value)}
                                >
                                  <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary ${isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50'}`}>
                                    {isSelected && <Check className="h-3 w-3" />}
                                  </div>
                                  <span className="flex-1">{role.label}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ({roleUserCount} users)
                                  </span>
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    {assignmentForm.roles.length > 0 
                      ? `${getSelectedUsersCount()} users will be assigned`
                      : 'Select one or more roles to assign this course'}
                  </p>
                </div>
              )}

              {assignmentForm.assign_to === 'users' && (
                <div className="space-y-2">
                  <Label>Select Users</Label>
                  <ScrollArea className="h-48 border rounded-lg p-2">
                    {users.map(u => (
                      <div key={u.id} className="flex items-center gap-2 p-2 hover:bg-muted rounded">
                        <input
                          type="checkbox"
                          id={`user-${u.id}`}
                          checked={assignmentForm.user_ids.includes(u.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setAssignmentForm(prev => ({ ...prev, user_ids: [...prev.user_ids, u.id] }));
                            } else {
                              setAssignmentForm(prev => ({ ...prev, user_ids: prev.user_ids.filter(id => id !== u.id) }));
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <label htmlFor={`user-${u.id}`} className="flex-1 cursor-pointer">
                          <span className="font-medium">{u.name}</span>
                          <span className="text-xs text-muted-foreground ml-2">{u.email}</span>
                        </label>
                        <Badge variant="outline" className="text-xs">{u.role}</Badge>
                      </div>
                    ))}
                  </ScrollArea>
                  <p className="text-xs text-muted-foreground">
                    {assignmentForm.user_ids.length} users selected
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label>Due Date <span className="text-destructive">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${!assignmentForm.due_date && 'text-muted-foreground'}`}
                      data-testid="assign-due-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {assignmentForm.due_date 
                        ? format(new Date(assignmentForm.due_date), 'PPP')
                        : 'Select due date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={assignmentForm.due_date ? new Date(assignmentForm.due_date) : undefined}
                      onSelect={(date) => setAssignmentForm(prev => ({ 
                        ...prev, 
                        due_date: date ? format(date, 'yyyy-MM-dd') : '' 
                      }))}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                    {assignmentForm.due_date && (
                      <div className="p-2 border-t">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full text-muted-foreground"
                          onClick={() => setAssignmentForm(prev => ({ ...prev, due_date: '' }))}
                        >
                          Clear date
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  Users will receive reminders as due date approaches
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAssignCourse} disabled={assigning} data-testid="assign-course-btn">
                {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Assign Course
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Course Stats Dialog */}
        <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-500" />
                Course Statistics
              </DialogTitle>
              <DialogDescription>
                {selectedCourse?.title} - Enrollment & completion report
              </DialogDescription>
            </DialogHeader>
            
            {loadingStats ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : courseStats ? (
              <div className="space-y-4 overflow-y-auto flex-1 min-h-0">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <Card className="p-3">
                    <p className="text-xs text-muted-foreground">Total Assigned</p>
                    <p className="text-2xl font-bold">{courseStats.summary.total_assigned}</p>
                  </Card>
                  <Card className="p-3 bg-green-500/10 border-green-500/20">
                    <p className="text-xs text-green-600">Completed</p>
                    <p className="text-2xl font-bold text-green-600">{courseStats.summary.completed}</p>
                  </Card>
                  <Card className="p-3 bg-blue-500/10 border-blue-500/20">
                    <p className="text-xs text-blue-600">In Progress</p>
                    <p className="text-2xl font-bold text-blue-600">{courseStats.summary.in_progress}</p>
                  </Card>
                  <Card className="p-3 bg-gray-500/10 border-gray-500/20">
                    <p className="text-xs text-gray-600">Not Started</p>
                    <p className="text-2xl font-bold text-gray-600">{courseStats.summary.not_started}</p>
                  </Card>
                  <Card className="p-3 bg-red-500/10 border-red-500/20">
                    <p className="text-xs text-red-600">Overdue</p>
                    <p className="text-2xl font-bold text-red-600">{courseStats.summary.overdue}</p>
                  </Card>
                </div>

                {/* Completion Rate */}
                <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium mb-1">Completion Rate</p>
                    <Progress value={courseStats.summary.completion_rate} className="h-2" />
                  </div>
                  <p className="text-lg font-bold">{courseStats.summary.completion_rate}%</p>
                </div>

                {/* Enrollments Table */}
                {courseStats.enrollments.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Progress</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Assigned</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {courseStats.enrollments.map((enrollment, idx) => (
                          <TableRow key={idx} className={enrollment.is_overdue ? 'bg-red-50 dark:bg-red-950/20' : ''}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  {enrollment.user_avatar && <AvatarImage src={enrollment.user_avatar} />}
                                  <AvatarFallback>{enrollment.user_name?.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium text-sm">{enrollment.user_name}</p>
                                  <p className="text-xs text-muted-foreground">{enrollment.user_email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {enrollment.user_role?.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant={
                                  enrollment.status === 'completed' ? 'default' :
                                  enrollment.is_overdue ? 'destructive' :
                                  enrollment.status === 'in_progress' ? 'secondary' : 'outline'
                                }
                                className="text-xs"
                              >
                                {enrollment.is_overdue ? 'Overdue' : enrollment.status?.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Progress value={enrollment.progress} className="h-1.5 w-16" />
                                <span className="text-xs">{enrollment.progress}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs">
                              {enrollment.due_date ? new Date(enrollment.due_date).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {enrollment.assigned_at ? new Date(enrollment.assigned_at).toLocaleDateString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No users have been assigned to this course yet</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Failed to load statistics
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatsDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Video Generation Dialog */}
        <Dialog open={videoGenDialogOpen} onOpenChange={setVideoGenDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                AI Video Generator (Sora 2)
              </DialogTitle>
              <DialogDescription>
                Generate training videos from text descriptions using AI
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Video Title</Label>
                <Input
                  value={videoGenForm.title}
                  onChange={(e) => setVideoGenForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Customer Service Best Practices"
                  data-testid="video-gen-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Video Description / Script</Label>
                <Textarea
                  value={videoGenForm.prompt}
                  onChange={(e) => setVideoGenForm(prev => ({ ...prev, prompt: e.target.value }))}
                  placeholder="Describe what you want in the video. Be specific about scenes, actions, and visual style..."
                  rows={4}
                  data-testid="video-gen-prompt"
                />
                <p className="text-xs text-muted-foreground">
                  Tip: Be descriptive. Example: &quot;A professional office environment where a customer service representative greets a customer with a warm smile, demonstrates active listening, and resolves their concern efficiently.&quot;
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Select
                    value={videoGenForm.duration.toString()}
                    onValueChange={(v) => setVideoGenForm(prev => ({ ...prev, duration: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="video-gen-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 seconds</SelectItem>
                      <SelectItem value="8">8 seconds</SelectItem>
                      <SelectItem value="12">12 seconds</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Resolution</Label>
                  <Select
                    value={videoGenForm.size}
                    onValueChange={(v) => setVideoGenForm(prev => ({ ...prev, size: v }))}
                  >
                    <SelectTrigger data-testid="video-gen-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1280x720">720p (16:9)</SelectItem>
                      <SelectItem value="1792x1024">1792x1024 (Wide)</SelectItem>
                      <SelectItem value="1024x1024">1024x1024 (Square)</SelectItem>
                      <SelectItem value="1024x1792">1024x1792 (Portrait)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {generatingVideo && (
                <Card className="bg-purple-500/10 border-purple-500/20">
                  <CardContent className="p-4 text-center">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-purple-500 mb-2" />
                    <p className="font-medium">Generating Video...</p>
                    <p className="text-sm text-muted-foreground">This may take a few minutes</p>
                  </CardContent>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setVideoGenDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleGenerateVideo}
                disabled={generatingVideo || !videoGenForm.prompt || !videoGenForm.title}
                data-testid="generate-video-btn"
              >
                {generatingVideo ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Generate Video
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Course Generation Dialog */}
        <Dialog open={aiCourseDialogOpen} onOpenChange={setAiCourseDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                {t('management.generateAICourse')}
              </DialogTitle>
              <DialogDescription>
                {t('management.aiCourseDescription', 'AI will generate a complete course with modules and quiz based on your topic. The course will be saved as a draft for your review.')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="ai-topic">{t('management.courseTopic', 'Course Topic')} *</Label>
                <Input
                  id="ai-topic"
                  placeholder={t('management.courseTopicPlaceholder', 'e.g., Customer Service Excellence, Sales Techniques...')}
                  value={aiCourseForm.topic}
                  onChange={(e) => setAiCourseForm(prev => ({ ...prev, topic: e.target.value }))}
                  data-testid="ai-course-topic"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={aiCourseForm.category} 
                    onValueChange={(v) => setAiCourseForm(prev => ({ ...prev, category: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Difficulty</Label>
                  <Select 
                    value={aiCourseForm.difficulty} 
                    onValueChange={(v) => setAiCourseForm(prev => ({ ...prev, difficulty: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Input
                  placeholder="e.g., store employees, managers..."
                  value={aiCourseForm.target_audience}
                  onChange={(e) => setAiCourseForm(prev => ({ ...prev, target_audience: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  min="10"
                  max="120"
                  value={aiCourseForm.duration_minutes}
                  onChange={(e) => setAiCourseForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))}
                />
              </div>
              {generatingAiCourse && (
                <Card className="bg-purple-500/10 border-purple-500/20">
                  <CardContent className="p-4 text-center">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-purple-500 mb-2" />
                    <p className="font-medium">Generating Course...</p>
                    <p className="text-sm text-muted-foreground">AI is creating modules and quiz questions</p>
                  </CardContent>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAiCourseDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleGenerateAiCourse}
                disabled={generatingAiCourse || !aiCourseForm.topic.trim()}
                className="bg-purple-500 hover:bg-purple-600"
                data-testid="generate-ai-course-submit"
              >
                {generatingAiCourse ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Brain className="w-4 h-4 mr-2" />
                )}
                Generate Course
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Course Translation Dialog */}
        <Dialog open={translateDialogOpen} onOpenChange={setTranslateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-500" />
                Translate Course
              </DialogTitle>
              <DialogDescription>
                {translateCourse && `Translate "${translateCourse.title}" to another language using AI.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Target Language *</Label>
                <Select 
                  value={translateForm.target_language} 
                  onValueChange={(v) => setTranslateForm(prev => ({ ...prev, target_language: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_LANGUAGES.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>What to Translate</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="translate-title"
                      checked={translateForm.fields.includes('title')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setTranslateForm(prev => ({ ...prev, fields: [...prev.fields, 'title'] }));
                        } else {
                          setTranslateForm(prev => ({ ...prev, fields: prev.fields.filter(f => f !== 'title') }));
                        }
                      }}
                    />
                    <Label htmlFor="translate-title" className="text-sm font-normal">Course Title</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="translate-description"
                      checked={translateForm.fields.includes('description')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setTranslateForm(prev => ({ ...prev, fields: [...prev.fields, 'description'] }));
                        } else {
                          setTranslateForm(prev => ({ ...prev, fields: prev.fields.filter(f => f !== 'description') }));
                        }
                      }}
                    />
                    <Label htmlFor="translate-description" className="text-sm font-normal">Course Description</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="translate-modules"
                      checked={translateForm.fields.includes('modules')}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setTranslateForm(prev => ({ ...prev, fields: [...prev.fields, 'modules'] }));
                        } else {
                          setTranslateForm(prev => ({ ...prev, fields: prev.fields.filter(f => f !== 'modules') }));
                        }
                      }}
                    />
                    <Label htmlFor="translate-modules" className="text-sm font-normal">Module Content</Label>
                  </div>
                </div>
              </div>
              {translating && (
                <Card className="bg-blue-500/10 border-blue-500/20">
                  <CardContent className="p-4 text-center">
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-500 mb-2" />
                    <p className="font-medium">Translating Course...</p>
                    <p className="text-sm text-muted-foreground">AI is translating your content</p>
                  </CardContent>
                </Card>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTranslateDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleTranslateCourse}
                disabled={translating || translateForm.fields.length === 0}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="translate-course-submit"
              >
                {translating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Translate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Standalone Quiz Dialog */}
        <Dialog open={standaloneQuizDialogOpen} onOpenChange={(open) => handleDialogClose('standalone-quiz', open)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                {standaloneQuizStep === 1 ? 'Create Standalone Quiz' : 'Assign Quiz to Users'}
              </DialogTitle>
              <DialogDescription>
                {standaloneQuizStep === 1 
                  ? 'Create a quiz that can be assigned to users independently'
                  : 'Select users or roles to assign this quiz'}
              </DialogDescription>
              {/* Step indicator */}
              <div className="flex items-center gap-2 pt-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${standaloneQuizStep >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>1</div>
                <div className={`flex-1 h-1 ${standaloneQuizStep >= 2 ? 'bg-primary' : 'bg-muted'}`}></div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${standaloneQuizStep >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>2</div>
              </div>
            </DialogHeader>
            
            {/* Step 1: Quiz Details */}
            {standaloneQuizStep === 1 && (
              <div className="space-y-6 py-4">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="sq-title">Quiz Title *</Label>
                      <Input
                        id="sq-title"
                        value={standaloneQuizForm.title}
                        onChange={(e) => setStandaloneQuizForm(prev => ({...prev, title: e.target.value}))}
                        placeholder="Enter quiz title"
                        data-testid="standalone-quiz-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sq-category">Category</Label>
                      <select
                        id="sq-category"
                        value={standaloneQuizForm.category}
                        onChange={(e) => setStandaloneQuizForm(prev => ({...prev, category: e.target.value}))}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        <option value="Assessment">Assessment</option>
                        <option value="Training">Training</option>
                        <option value="Knowledge Check">Knowledge Check</option>
                        <option value="Certification">Certification</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="sq-description">Description</Label>
                    <Textarea
                      id="sq-description"
                      value={standaloneQuizForm.description}
                      onChange={(e) => setStandaloneQuizForm(prev => ({...prev, description: e.target.value}))}
                      placeholder="Enter quiz description"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Quiz Settings */}
                <div className="border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Quiz Settings
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Passing Score (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={standaloneQuizForm.passing_score}
                        onChange={(e) => setStandaloneQuizForm(prev => ({...prev, passing_score: parseInt(e.target.value) || 70}))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Time Limit (mins)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={standaloneQuizForm.time_limit}
                        onChange={(e) => setStandaloneQuizForm(prev => ({...prev, time_limit: parseInt(e.target.value) || 0}))}
                        placeholder="0 = no limit"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Attempts</Label>
                      <Input
                        type="number"
                        min="0"
                        value={standaloneQuizForm.max_attempts}
                        onChange={(e) => setStandaloneQuizForm(prev => ({...prev, max_attempts: parseInt(e.target.value) || 0}))}
                        placeholder="0 = unlimited"
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Switch
                        checked={standaloneQuizForm.show_correct_answers}
                        onCheckedChange={(checked) => setStandaloneQuizForm(prev => ({...prev, show_correct_answers: checked}))}
                      />
                      <Label>Show Answers</Label>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        checked={standaloneQuizForm.shuffle_questions}
                        onCheckedChange={(checked) => setStandaloneQuizForm(prev => ({...prev, shuffle_questions: checked}))}
                      />
                      <span className="text-sm">Shuffle Questions</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Switch
                        checked={standaloneQuizForm.shuffle_options}
                        onCheckedChange={(checked) => setStandaloneQuizForm(prev => ({...prev, shuffle_options: checked}))}
                      />
                      <span className="text-sm">Shuffle Options</span>
                    </label>
                  </div>
                </div>

                {/* Questions */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium flex items-center gap-2">
                      <ListOrdered className="h-4 w-4" />
                      Questions ({standaloneQuizForm.questions.length})
                    </h4>
                    <DropdownMenu open={questionTypeMenuOpen} onOpenChange={setQuestionTypeMenuOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" data-testid="add-question-dropdown">
                          <Plus className="h-4 w-4 mr-1" />
                          Add Question
                          <ChevronsUpDown className="h-4 w-4 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem 
                          onClick={() => { handleAddStandaloneQuestion('single_choice'); setQuestionTypeMenuOpen(false); }}
                          className="cursor-pointer"
                          data-testid="add-single-choice-question"
                        >
                          <CircleDot className="w-4 h-4 mr-2" />
                          Single Choice
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => { handleAddStandaloneQuestion('multiple_choice'); setQuestionTypeMenuOpen(false); }}
                          className="cursor-pointer"
                          data-testid="add-multiple-choice-question"
                        >
                          <CheckSquare className="w-4 h-4 mr-2" />
                          Multiple Choice
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => { handleAddStandaloneQuestion('true_false'); setQuestionTypeMenuOpen(false); }}
                          className="cursor-pointer"
                          data-testid="add-true-false-question"
                        >
                          <ToggleLeft className="w-4 h-4 mr-2" />
                          True/False
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => { handleAddStandaloneQuestion('match_following'); setQuestionTypeMenuOpen(false); }}
                          className="cursor-pointer"
                          data-testid="add-match-following-question"
                        >
                          <GitMerge className="w-4 h-4 mr-2" />
                          Match the Following
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {standaloneQuizForm.questions.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                      <HelpCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No questions added yet</p>
                      <p className="text-sm">Click "Add Question" to create your first question</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {standaloneQuizForm.questions.map((q, qIndex) => (
                        <div key={q.id} className="border rounded-lg p-4 bg-muted/30">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">Question {qIndex + 1}</span>
                              <Badge variant="outline" className="text-xs">
                                {q.type === 'single_choice' ? 'Single Choice' :
                                 q.type === 'multiple_choice' ? 'Multiple Choice' :
                                 q.type === 'true_false' ? 'True/False' :
                                 q.type === 'match_following' ? 'Match Following' : 'Single Choice'}
                              </Badge>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleRemoveStandaloneQuestion(qIndex)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="space-y-3">
                            <Textarea
                              value={q.question}
                              onChange={(e) => handleUpdateStandaloneQuestion(qIndex, 'question', e.target.value)}
                              placeholder="Enter question..."
                              rows={2}
                            />
                            
                            {/* Single Choice / Multiple Choice */}
                            {(q.type === 'single_choice' || q.type === 'multiple_choice' || !q.type) && q.options && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {q.options.map((opt, optIndex) => (
                                  <div key={optIndex} className="flex items-center gap-2">
                                    {q.type === 'multiple_choice' ? (
                                      <input
                                        type="checkbox"
                                        checked={Array.isArray(q.correct_answer) ? q.correct_answer.includes(optIndex) : q.correct_answer === optIndex}
                                        onChange={(e) => {
                                          let newAnswer;
                                          if (Array.isArray(q.correct_answer)) {
                                            if (e.target.checked) {
                                              newAnswer = [...q.correct_answer, optIndex];
                                            } else {
                                              newAnswer = q.correct_answer.filter(i => i !== optIndex);
                                            }
                                          } else {
                                            newAnswer = e.target.checked ? [optIndex] : [];
                                          }
                                          handleUpdateStandaloneQuestion(qIndex, 'correct_answer', newAnswer);
                                        }}
                                        className="w-4 h-4"
                                      />
                                    ) : (
                                      <input
                                        type="radio"
                                        name={`correct-${q.id}`}
                                        checked={q.correct_answer === optIndex}
                                        onChange={() => handleUpdateStandaloneQuestion(qIndex, 'correct_answer', optIndex)}
                                        className="w-4 h-4"
                                      />
                                    )}
                                    <Input
                                      value={opt}
                                      onChange={(e) => {
                                        const newOptions = [...q.options];
                                        newOptions[optIndex] = e.target.value;
                                        handleUpdateStandaloneQuestion(qIndex, 'options', newOptions);
                                      }}
                                      placeholder={`Option ${optIndex + 1}`}
                                      className="flex-1"
                                    />
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* True/False */}
                            {q.type === 'true_false' && (
                              <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`correct-${q.id}`}
                                    checked={q.correct_answer === 0}
                                    onChange={() => handleUpdateStandaloneQuestion(qIndex, 'correct_answer', 0)}
                                    className="w-4 h-4"
                                  />
                                  <span>True</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                  <input
                                    type="radio"
                                    name={`correct-${q.id}`}
                                    checked={q.correct_answer === 1}
                                    onChange={() => handleUpdateStandaloneQuestion(qIndex, 'correct_answer', 1)}
                                    className="w-4 h-4"
                                  />
                                  <span>False</span>
                                </label>
                              </div>
                            )}
                            
                            {/* Match the Following */}
                            {q.type === 'match_following' && q.pairs && (
                              <div className="space-y-2">
                                <div className="grid grid-cols-2 gap-2 text-sm font-medium text-muted-foreground">
                                  <span>Left Column</span>
                                  <span>Right Column (Correct Match)</span>
                                </div>
                                {q.pairs.map((pair, pairIndex) => (
                                  <div key={pairIndex} className="grid grid-cols-2 gap-2">
                                    <Input
                                      value={pair.left}
                                      onChange={(e) => {
                                        const newPairs = [...q.pairs];
                                        newPairs[pairIndex].left = e.target.value;
                                        handleUpdateStandaloneQuestion(qIndex, 'pairs', newPairs);
                                      }}
                                      placeholder={`Item ${pairIndex + 1}`}
                                    />
                                    <Input
                                      value={pair.right}
                                      onChange={(e) => {
                                        const newPairs = [...q.pairs];
                                        newPairs[pairIndex].right = e.target.value;
                                        handleUpdateStandaloneQuestion(qIndex, 'pairs', newPairs);
                                      }}
                                      placeholder={`Match ${pairIndex + 1}`}
                                    />
                                  </div>
                                ))}
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    const newPairs = [...q.pairs, { left: '', right: '' }];
                                    handleUpdateStandaloneQuestion(qIndex, 'pairs', newPairs);
                                  }}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Add Pair
                                </Button>
                              </div>
                            )}
                            
                            <Input
                              value={q.explanation || ''}
                              onChange={(e) => handleUpdateStandaloneQuestion(qIndex, 'explanation', e.target.value)}
                              placeholder="Explanation (optional)"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Assignment */}
            {standaloneQuizStep === 2 && (
              <div className="space-y-6 py-4">
                {/* Info banner */}
                <div className="bg-muted/50 border rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">
                    Assignment is optional. You can skip this step and assign the quiz later from the quiz card.
                  </p>
                </div>

                {/* Publish Quiz Toggle */}
                <div className="flex items-center justify-between border rounded-lg p-4">
                  <div>
                    <Label className="text-base font-medium">Publish Quiz</Label>
                    <p className="text-sm text-muted-foreground">Make this quiz visible to learners</p>
                  </div>
                  <Switch
                    checked={standaloneQuizForm.is_published}
                    onCheckedChange={(checked) => setStandaloneQuizForm(prev => ({...prev, is_published: checked}))}
                  />
                </div>

                {/* Assign to Roles - Dropdown with Search */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Assign to Roles</Label>
                  <Popover open={roleSelectOpen} onOpenChange={setRoleSelectOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between h-auto min-h-10 py-2"
                        data-testid="role-select-trigger"
                      >
                        <span className="text-left">
                          {standaloneQuizAssignment.roles.length === 0 
                            ? "Select roles..." 
                            : standaloneQuizAssignment.roles.length === ACTUAL_ROLES.length
                              ? "All roles selected"
                              : `${standaloneQuizAssignment.roles.length} role(s) selected`}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[400px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search roles..." />
                        <CommandList>
                          <CommandEmpty>No roles found.</CommandEmpty>
                          <CommandGroup>
                            {/* Select All Option */}
                            <CommandItem
                              onSelect={() => {
                                const allSelected = standaloneQuizAssignment.roles.length === ACTUAL_ROLES.length;
                                setStandaloneQuizAssignment(prev => ({
                                  ...prev,
                                  roles: allSelected ? [] : ALL_ROLE_VALUES
                                }));
                              }}
                              className="cursor-pointer"
                            >
                              <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${standaloneQuizAssignment.roles.length === ACTUAL_ROLES.length ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground'}`}>
                                {standaloneQuizAssignment.roles.length === ACTUAL_ROLES.length && <Check className="h-3 w-3" />}
                              </div>
                              <span className="font-medium">Select All</span>
                            </CommandItem>
                            <Separator className="my-1" />
                            {/* Individual Roles */}
                            {ACTUAL_ROLES.map((role) => (
                              <CommandItem
                                key={role.value}
                                onSelect={() => {
                                  const isSelected = standaloneQuizAssignment.roles.includes(role.value);
                                  setStandaloneQuizAssignment(prev => ({
                                    ...prev,
                                    roles: isSelected 
                                      ? prev.roles.filter(r => r !== role.value)
                                      : [...prev.roles, role.value]
                                  }));
                                }}
                                className="cursor-pointer"
                              >
                                <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${standaloneQuizAssignment.roles.includes(role.value) ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground'}`}>
                                  {standaloneQuizAssignment.roles.includes(role.value) && <Check className="h-3 w-3" />}
                                </div>
                                <span>{role.label}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  
                  {/* Selected roles display */}
                  {standaloneQuizAssignment.roles.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {standaloneQuizAssignment.roles.map(roleVal => {
                        const roleInfo = ACTUAL_ROLES.find(r => r.value === roleVal);
                        return (
                          <Badge key={roleVal} variant="secondary" className="text-xs">
                            {roleInfo?.label || roleVal}
                            <button
                              type="button"
                              className="ml-1 hover:text-destructive"
                              onClick={() => setStandaloneQuizAssignment(prev => ({
                                ...prev,
                                roles: prev.roles.filter(r => r !== roleVal)
                              }))}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Due Date */}
                <div className="space-y-2">
                  <Label htmlFor="due-date">Due Date (Optional)</Label>
                  <Input
                    id="due-date"
                    type="date"
                    value={standaloneQuizAssignment.due_date}
                    onChange={(e) => setStandaloneQuizAssignment(prev => ({...prev, due_date: e.target.value}))}
                    className="w-full"
                  />
                </div>
              </div>
            )}

            <DialogFooter className="flex flex-col sm:flex-row gap-2">
              {standaloneQuizStep === 1 ? (
                <>
                  <Button variant="outline" onClick={() => handleDialogClose('standalone-quiz', false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveStandaloneQuiz}
                    disabled={savingQuiz || !standaloneQuizForm.title || standaloneQuizForm.questions.length === 0}
                    data-testid="save-standalone-quiz-btn"
                  >
                    {savingQuiz ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ChevronRight className="h-4 w-4 mr-2" />
                    )}
                    Next: Assign Quiz
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setStandaloneQuizStep(1)} className="gap-1">
                    <ChevronRight className="h-4 w-4 rotate-180" />
                    Back
                  </Button>
                  <Button
                    onClick={handleAssignStandaloneQuiz}
                    disabled={assigning}
                    data-testid="assign-standalone-quiz-btn"
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    {assigning ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Finish & Assign
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm Close Dialog */}
        <Dialog open={confirmCloseDialog.open} onOpenChange={(open) => !open && setConfirmCloseDialog({ open: false, type: null })}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Discard Changes?</DialogTitle>
              <DialogDescription>
                You have unsaved changes. Are you sure you want to close? All changes will be lost.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirmCloseDialog({ open: false, type: null })}>
                No, Keep Editing
              </Button>
              <Button variant="destructive" onClick={confirmDialogClose}>
                Yes, Discard
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Quiz Analytics Dialog */}
        <Dialog open={quizAnalyticsOpen} onOpenChange={setQuizAnalyticsOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-purple-500" />
                Quiz Analytics
              </DialogTitle>
              <DialogDescription>
                {quizAnalytics?.quiz_title || 'Loading...'}
              </DialogDescription>
            </DialogHeader>
            
            {loadingAnalytics ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : quizAnalytics ? (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-primary">{quizAnalytics.summary.total_assigned}</p>
                      <p className="text-xs text-muted-foreground">Total Assigned</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{quizAnalytics.summary.completion_rate}%</p>
                      <p className="text-xs text-muted-foreground">Completion Rate</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-amber-600">{quizAnalytics.summary.avg_score}%</p>
                      <p className="text-xs text-muted-foreground">Average Score</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-purple-600">{quizAnalytics.summary.pass_rate}%</p>
                      <p className="text-xs text-muted-foreground">Pass Rate</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Progress Breakdown */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Progress Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span>Completed: {quizAnalytics.summary.completed}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <span>In Progress: {quizAnalytics.summary.in_progress}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-gray-300" />
                        <span>Pending: {quizAnalytics.summary.pending}</span>
                      </div>
                    </div>
                    <Progress 
                      value={quizAnalytics.summary.completion_rate} 
                      className="mt-3 h-2"
                    />
                  </CardContent>
                </Card>

                {/* Score Stats */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Score Statistics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xl font-bold text-green-600">{quizAnalytics.summary.max_score}%</p>
                        <p className="text-xs text-muted-foreground">Highest Score</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-amber-600">{quizAnalytics.summary.avg_score}%</p>
                        <p className="text-xs text-muted-foreground">Average Score</p>
                      </div>
                      <div>
                        <p className="text-xl font-bold text-red-600">{quizAnalytics.summary.min_score}%</p>
                        <p className="text-xs text-muted-foreground">Lowest Score</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 text-center">
                      Passing Score: {quizAnalytics.passing_score}% | Total Attempts: {quizAnalytics.summary.total_attempts}
                    </p>
                  </CardContent>
                </Card>

                {/* User Performance Table */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">User Performance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {quizAnalytics.user_stats.length > 0 ? (
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left py-2 px-3 font-medium">User</th>
                              <th className="text-center py-2 px-3 font-medium">Status</th>
                              <th className="text-center py-2 px-3 font-medium">Attempts</th>
                              <th className="text-center py-2 px-3 font-medium">Best Score</th>
                              <th className="text-center py-2 px-3 font-medium">Result</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {quizAnalytics.user_stats.map((user, idx) => (
                              <tr key={idx} className="hover:bg-muted/30">
                                <td className="py-2 px-3">
                                  <span className="font-medium">{user.user_name || 'Unknown'}</span>
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <Badge 
                                    variant={user.status === 'completed' ? 'default' : user.status === 'in_progress' ? 'secondary' : 'outline'}
                                    className={user.status === 'completed' ? 'bg-green-600' : ''}
                                  >
                                    {user.status === 'completed' ? 'Completed' : user.status === 'in_progress' ? 'In Progress' : 'Pending'}
                                  </Badge>
                                </td>
                                <td className="py-2 px-3 text-center">{user.attempts}</td>
                                <td className="py-2 px-3 text-center">
                                  {user.best_score !== null ? `${user.best_score}%` : '-'}
                                </td>
                                <td className="py-2 px-3 text-center">
                                  {user.best_score !== null ? (
                                    user.passed ? (
                                      <Badge className="bg-green-600">Passed</Badge>
                                    ) : (
                                      <Badge variant="destructive">Failed</Badge>
                                    )
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>No users assigned yet</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <XCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Failed to load analytics</p>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setQuizAnalyticsOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Course Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Course</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>"{courseToDelete?.title}"</strong>?
                <br /><br />
                This action cannot be undone. The course and all its content will be permanently removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDeleteCourse(courseToDelete?.id, courseToDelete?.title)}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Course
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Slide Editor Full-Screen Overlay */}
        {slideEditorOpen && (
          <SlideEditor
            initialData={moduleForm.content || null}
            onSave={(data) => {
              setModuleForm(prev => ({ ...prev, content: data }));
              setSlideEditorOpen(false);
            }}
            onClose={() => setSlideEditorOpen(false)}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default CourseManagement;
